// Copyright 2016 Sorama B.V.

import { autoinject } from "aurelia-framework";
import {
    NewProjectDTO,
    NewTimelineEntryDTO,
    ProjectCanvasCollectionDTO,
    ProjectDTO,
    TimelineEntryDTO,
    TimelineEntryType,
} from "datatypes/SoramaWebApi";
import { Api } from "libraries/WebApi/Api";
import { IPagedRequest, IPageResult, IWebRequestHandler } from "libraries/WebRequestHandler/IWebRequestHandler";
import { PagedRequest } from "libraries/WebRequestHandler/PagedRequest";
import { GenericWebApiRepository } from "repositories/GenericRepository";

export abstract class IProjectRepository {
    /**
     * Gets a paged requests returning all timeline entries for the provided project (id).
     * @param projectId Id of the project to retrieve Timeline entries for.
     */
    public abstract getTimelineEntries(projectId: string): IPagedRequest<TimelineEntryDTO>;

    /**
     * Creates a new timeline entry for the provided project (id).
     * @param projectId Id of the proejct to create the new timeline entry for.
     * @param type Type of timeline entry to create.
     * @param properties Properties to store with the timeline entry. Can be any dictionary like object.
     */
    public abstract createTimelineEntry(
        projectId: string,
        type: TimelineEntryType,
        properties: { [key: string]: unknown }
    ): Promise<TimelineEntryDTO>;

    /**
     * Gets a paged requests returning all timeline entries for the provided project (id).
     * @param projectId Id of the project to retrieve Timeline entries for.
     */
    public abstract getJournals(projectId: string, filter: string): IPagedRequest<ProjectCanvasCollectionDTO>;

    /**
     * Creates a new journal for a project (id)
     * @param projectId Id of the project to create the journal in.
     * @param name Name of the journal.
     */
    public abstract createJournal(projectId: string, name: string): Promise<ProjectCanvasCollectionDTO>;

    /**
     * Gets a paged requests returning all timeline entries for the provided project (id).
     * @param projectId Id of the project to retrieve Timeline entries for.
     */
    public abstract getPresentations(projectId: string, filter: string): IPagedRequest<ProjectCanvasCollectionDTO>;

    /**
     * Creates a new presentation for a project (id)
     * @param projectId Id of the project to create the presentation in.
     * @param name Name of the presentation.
     */
    public abstract createPresentation(projectId: string, name: string): Promise<ProjectCanvasCollectionDTO>;
}

@autoinject
export class ProjectRepository
    extends GenericWebApiRepository<ProjectDTO, NewProjectDTO>
    implements IProjectRepository
{
    constructor(webRequestHandler: IWebRequestHandler) {
        super(webRequestHandler, Api.projects);
    }

    protected equals(a: ProjectDTO, b: ProjectDTO) {
        return (
            a === b ||
            (a.id === b.id &&
                a.name === b.name &&
                a.description === b.description &&
                a.ownerId === b.ownerId &&
                a.created === b.created)
        );
    }

    public getTimelineEntries(projectId: string): IPagedRequest<TimelineEntryDTO> {
        return PagedRequest.create<TimelineEntryDTO>(
            this.webRequestHandler.createGetRequest<IPageResult<TimelineEntryDTO>>(
                Api.projects + "/" + projectId + "/TimelineEntries"
            )
        );
    }

    public getHighlightedTimelineEntries(projectId: string): IPagedRequest<TimelineEntryDTO> {
        const filterString = "?$filter=highlighted eq true";
        return PagedRequest.create<TimelineEntryDTO>(
            this.webRequestHandler.createGetRequest<IPageResult<TimelineEntryDTO>>(
                Api.projects + "/" + projectId + "/TimelineEntries" + filterString
            )
        );
    }

    public createTimelineEntry(
        projectId: string,
        type: TimelineEntryType,
        properties: { [key: string]: unknown }
    ): Promise<TimelineEntryDTO> {
        return this.webRequestHandler
            .createPostRequest<TimelineEntryDTO>(Api.projects + "/" + projectId + "/TimelineEntries", <
                NewTimelineEntryDTO
            >{
                type: type,
                properties: properties,
            })
            .send()
            .then((response) => {
                return response.content;
            });
    }

    public getJournals(projectId: string, filter = ""): IPagedRequest<ProjectCanvasCollectionDTO> {
        const anyQuery = filter ? "/Journals" + "?" + filter : "/Journals";
        const request = this.webRequestHandler.createGetRequest<IPageResult<ProjectCanvasCollectionDTO>>(
            Api.projects + "/" + projectId + anyQuery
        );
        return PagedRequest.create(request);
    }

    public createJournal(projectId: string, name: string): Promise<ProjectCanvasCollectionDTO> {
        return this.webRequestHandler
            .createPostRequest<ProjectCanvasCollectionDTO>(Api.projects + "/" + projectId + "/Journals", <
                ProjectCanvasCollectionDTO
            >{ name: name })
            .send()
            .then((x) => x.content);
    }

    public getPresentations(projectId: string, filter = ""): IPagedRequest<ProjectCanvasCollectionDTO> {
        const anyQuery = filter ? "/Presentations" + "?" + filter : "/Presentations";
        const request = this.webRequestHandler.createGetRequest<IPageResult<ProjectCanvasCollectionDTO>>(
            Api.projects + "/" + projectId + anyQuery
        );
        return PagedRequest.create(request);
    }

    public createPresentation(projectId: string, name: string): Promise<ProjectCanvasCollectionDTO> {
        return this.webRequestHandler
            .createPostRequest<ProjectCanvasCollectionDTO>(Api.projects + "/" + projectId + "/Presentations", <
                ProjectCanvasCollectionDTO
            >{ name: name })
            .send()
            .then((x) => x.content);
    }

    public getFavoriteProjectIds(representativeId?: string): Promise<string[]> {
        const optionalRepresentativeIdParamString = representativeId ? "?representativeId=" + representativeId : "";
        return this.webRequestHandler
            .createGetRequest<string[]>(Api.projects + "/Favorites" + optionalRepresentativeIdParamString)
            .send()
            .then((x) => x.content);
    }

    public setFavoriteProject(projectId: string, favorite = true, representativeId?: string): Promise<void> {
        const optionalRepresentativeIdParamString = representativeId ? "?representativeId=" + representativeId : "";
        const favoriteEndpoint = favorite ? "Favorite" : "Unfavorite";
        return this.webRequestHandler
            .createPostRequest(
                Api.projects + "/" + projectId + "/" + favoriteEndpoint + optionalRepresentativeIdParamString
            )
            .send()
            .then(() => {});
    }
}
